import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, UserRoundPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchOrgs, fetchSigners } from "@/features/text-sign/api";
import { SIGN_ROLE_LABEL } from "@/features/text-sign/helpers";
import { cn } from "@/lib/utils";

export type PickedSigner = {
  staffId: number;
  fullName: string;
  loginName: string;
  orgName?: string | null;
  role: number;
  level: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onPick: (s: PickedSigner) => void;
  defaultLevel?: number;
  /**
   * "internal" (default): lock the org to the submitter's current org (user.orgId)
   * so only colleagues in the same unit appear.
   * "bypass": allow picking any org, used for "Bổ sung người ký vượt cấp".
   */
  mode?: "internal" | "bypass";
  defaultOrgId?: number;
};

export const SIGN_ROLES = [
  { id: 336952, label: "Lãnh đạo đơn vị (LDDV)" },
  { id: 336953, label: "Thường trực (TTDV)" },
  { id: 336954, label: "Văn thư (VT)" },
  { id: 336955, label: "Nhân viên (NV)" },
  { id: 336871, label: "Thư ký LĐ" },
];

export function SignerPickerDialog({
  open,
  onClose,
  onPick,
  defaultLevel = 1,
  mode = "internal",
  defaultOrgId,
}: Props) {
  const [orgQ, setOrgQ] = useState("");
  const [orgId, setOrgId] = useState<number | undefined>(
    mode === "internal" ? defaultOrgId : undefined,
  );
  const [roleId, setRoleId] = useState<number>(SIGN_ROLES[0].id);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState(defaultLevel);

  useEffect(() => {
    if (open) {
      setLevel(defaultLevel);
      setQ("");
      // Internal mode locks to user's org; bypass lets user pick freely.
      setOrgId(mode === "internal" ? defaultOrgId : undefined);
    }
  }, [open, defaultLevel, mode, defaultOrgId]);

  const orgs = useQuery({
    queryKey: ["sign-orgs", orgQ],
    queryFn: () => fetchOrgs(orgQ || undefined),
    staleTime: 60_000,
    enabled: open,
  });

  const signers = useQuery({
    queryKey: ["sign-signers", orgId, roleId, q],
    queryFn: () => fetchSigners({ orgId, role: roleId, q: q || undefined }),
    staleTime: 20_000,
    enabled: open,
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-3xl rounded-lg bg-background border border-border shadow-lifted"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <UserRoundPlus className="h-5 w-5 text-primary-600" />
            <h3 className="font-semibold">
              {mode === "bypass" ? "Bổ sung người ký vượt cấp" : "Bổ sung người ký nội bộ"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="h-7 w-7 grid place-items-center rounded hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Đơn vị</label>
              <input
                type="text"
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm mb-1"
                placeholder="Tìm đơn vị…"
                value={orgQ}
                onChange={(e) => setOrgQ(e.target.value)}
              />
              <select
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                value={orgId ?? ""}
                onChange={(e) => setOrgId(e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">—— Tất cả ——</option>
                {(orgs.data ?? []).map((o) => (
                  <option key={o.orgId} value={o.orgId}>
                    {o.name ?? o.code ?? `#${o.orgId}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Vai trò</label>
              <select
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                value={roleId}
                onChange={(e) => setRoleId(Number(e.target.value))}
              >
                {SIGN_ROLES.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Cấp ký</label>
              <input
                type="number"
                min={1}
                max={20}
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                value={level}
                onChange={(e) => setLevel(Number(e.target.value) || 1)}
              />
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              className="w-full h-9 pl-10 pr-3 rounded-md border border-input bg-background text-sm"
              placeholder="Tìm tên / login…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <div className="max-h-[45vh] overflow-auto border border-border rounded-md">
            {signers.isLoading && (
              <div className="p-4 text-sm text-muted-foreground">Đang tải danh sách…</div>
            )}
            {!signers.isLoading && (signers.data?.length ?? 0) === 0 && (
              <div className="p-4 text-sm text-muted-foreground">Không có người ký phù hợp.</div>
            )}
            <ul className="divide-y divide-border">
              {(signers.data ?? []).slice(0, 100).map((s) => (
                <li
                  key={s.staffId}
                  className={cn(
                    "flex items-center gap-3 px-4 py-2 hover:bg-muted/50 cursor-pointer",
                  )}
                  onClick={() => {
                    onPick({
                      staffId: s.staffId,
                      fullName: s.fullName,
                      loginName: s.loginName,
                      role: roleId,
                      level,
                    });
                    onClose();
                  }}
                >
                  <div className="h-8 w-8 rounded-full bg-primary-50 text-primary-600 grid place-items-center text-xs font-semibold">
                    {s.loginName?.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{s.fullName ?? s.loginName}</div>
                    <div className="text-xs text-muted-foreground">
                      {s.loginName} · {SIGN_ROLE_LABEL[roleId] ?? roleId} · Cấp {level}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  );
}
